import React from "react";
import StatusBadge from "./StatusBadge";
import TenantOverviewTab from "./TenantOverviewTab";
import TenantTeamTab from "./TenantTeamTab";
import TenantServicesTab from "./TenantServicesTab";
import TenantConfigTab from "./TenantConfigTab";
import OnboardingWizard from "./OnboardingWizard";
import TenantAuditTab from "./TenantAuditTab";

const allTabs = [
  { key: "overview", label: "Overview" },
  { key: "team", label: "Team" },
  { key: "services", label: "Services" },
  { key: "config", label: "Config" },
  { key: "onboarding", label: "Onboarding" },
  { key: "audit", label: "Audit" },
];

const TenantDetails = ({
  tenant,
  tenantId,
  activeTab,
  onTabChange,
  users,
  services,
  configs,
  onboarding,
  onboardingSteps,
  auditLogs,
  onServiceToggle,
  onStartOnboarding,
  onStepUpdate,
  onActivateTenant,
  onDeleteTenant,
  statusValue,
  onStatusValueChange,
  isDisabled,
  userRole,
  onBulkInvite,
  onBulkDeleteUsers,
  onUpdateUserRole,
  onDeleteUser,
  onConfigSave,
}) => {
  if (!tenant) return null;

  const tabs = allTabs;

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold text-slate-950">{tenant.name}</h1>
              <StatusBadge status={tenant.status} />
            </div>
            <p className="mt-2 text-sm text-slate-500">
              {tenant.slug} · {tenant.industry} · {tenant.business_type}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {!isDisabled ? (
              <>
                <select
                  value={statusValue}
                  onChange={(e) => onStatusValueChange(e.target.value)}
                  className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20"
                >
                  {["draft", "onboarding", "active", "inactive", "suspended", "deleted"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={onDeleteTenant}
                  className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-100"
                >
                  Delete tenant
                </button>
              </>
            ) : null}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => onTabChange(tab.key)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                activeTab === tab.key
                  ? "bg-[#eef5ff] text-[#096dea]"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {activeTab === "overview" ? <TenantOverviewTab tenant={tenant} tenantId={tenantId} onboardingSteps={onboardingSteps} /> : null}
      {activeTab === "team" ? (
        <TenantTeamTab
          users={users}
          disabled={isDisabled}
          onBulkInvite={onBulkInvite}
          onBulkDeleteUsers={onBulkDeleteUsers}
          onUpdateUserRole={onUpdateUserRole}
          onDeleteUser={onDeleteUser}
        />
      ) : null}
      {activeTab === "services" ? (
        onboarding ? (
          <TenantServicesTab tenantId={tenantId} services={services} onToggleService={onServiceToggle} disabled={isDisabled} />
        ) : (
          <div className="rounded-3xl border border-slate-200 bg-white p-10 shadow-sm text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-semibold text-slate-950">Onboarding required</h3>
            <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
              Start the onboarding process to configure services for this tenant.
            </p>
            <button
              type="button"
              onClick={() => { onStartOnboarding(); onTabChange("onboarding"); }}
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb]"
            >
              Start Onboarding
            </button>
          </div>
        )
      ) : null}
      {activeTab === "config" ? (
        onboarding ? (
          <TenantConfigTab configs={configs} disabled={isDisabled} onSave={onConfigSave} />
        ) : (
          <div className="rounded-3xl border border-slate-200 bg-white p-10 shadow-sm text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-semibold text-slate-950">Onboarding required</h3>
            <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
              Start the onboarding process to configure settings for this tenant.
            </p>
            <button
              type="button"
              onClick={() => { onStartOnboarding(); onTabChange("onboarding"); }}
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb]"
            >
              Start Onboarding
            </button>
          </div>
        )
      ) : null}
      {activeTab === "onboarding" ? (
        <OnboardingWizard
          onboarding={onboarding}
          steps={onboardingSteps}
          tenant={tenant}
          tenantId={tenantId}
          services={services}
          configs={configs}
          onStart={onStartOnboarding}
          onStepUpdate={onStepUpdate}
          onActivate={onActivateTenant}
          onToggleService={onServiceToggle}
          onConfigSave={onConfigSave}
          isDisabled={isDisabled}
        />
      ) : null}
      {activeTab === "audit" ? <TenantAuditTab auditLogs={auditLogs} /> : null}
    </div>
  );
};

export default TenantDetails;
